import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import apiService from "../../services/apiService";
import productService from "../../services/productService";
import { useProductsCrud } from "../../hooks/useProductsCrud";

const localCacheKey = "productReadCache";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function ReadProducts() {
  const navigate = useNavigate();

  const { deleteProduct, updateProduct } = useProductsCrud();

  // ✅ Safe read from localStorage
  const [cachedData, setCachedData] = useState(() => {
    try {
      const cache = localStorage.getItem(localCacheKey);
      return cache ? JSON.parse(cache) : null;
    } catch (error) {
      console.warn("Invalid JSON in localStorage. Clearing corrupted cache.");
      localStorage.removeItem(localCacheKey);
      return null;
    }
  });

  const [localData, setLocalData] = useState(cachedData?.products || []);
  const [loading, setLoading] = useState(!cachedData);
const [selectedIds, setSelectedIds] = useState([]); 
const isAllSelected =
  localData.length > 0 &&
  selectedIds.length === localData.length;
  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const enrichProducts = useCallback((products, categories, subCategories) => {
    return products.map((product, index) => {
      const category = categories.find((cat) => cat.id === product.category_id);
      const subCategory = subCategories.find((sub) => sub.id === product.sub_category_id);
      return {
        ...product,
        category_name: category ? category.name : "Unknown",
        sub_category_name: subCategory ? subCategory.name : "Unknown",
        sl: index + 1,
      };
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [productRes, categoryRes, subCategoryRes] = await Promise.all([
        productService.getAllProducts(),
        apiService.getAll("backend/categories"),
        apiService.getAll("backend/sub-categories"),
      ]);

      const products = productRes.data.data || productRes.data;
      const categories = categoryRes.data;
      const subCategories = subCategoryRes.data;

      const enriched = enrichProducts(products, categories, subCategories);

      const newCache = {
        products: enriched,
        categories,
        subCategories,
      };

      let existingCache = null;
      try {
        existingCache = JSON.parse(localStorage.getItem(localCacheKey));
      } catch (e) {
        console.warn("Existing cache is invalid. Replacing.");
      }

      if (
        !existingCache ||
        JSON.stringify(existingCache.products) !== JSON.stringify(enriched) ||
        JSON.stringify(existingCache.categories) !== JSON.stringify(categories) ||
        JSON.stringify(existingCache.subCategories) !== JSON.stringify(subCategories)
      ) {
        try {
          localStorage.setItem(localCacheKey, JSON.stringify(newCache));
        } catch (e) {
          console.error("Failed to write to localStorage", e);
        }
        setCachedData(newCache);
      }

      setLocalData(enriched);
    } catch (error) {
      console.error("Failed to load data:", error);
      showSwal("Error", "Failed to load product data.", "error");
    } finally {
      setLoading(false);
    }
  }, [enrichProducts, showSwal]);

  useEffect(() => {
    if (cachedData) {
      setLocalData(cachedData.products);
      setLoading(false);
      loadData(); // Background refresh
    } else {
      loadData();
    }
  }, [cachedData, loadData]);

  const updateLocalDataCache = (updatedProducts) => {
    if (!cachedData) return;
    const enriched = enrichProducts(updatedProducts, cachedData.categories, cachedData.subCategories);
    const newCache = {
      products: enriched,
      categories: cachedData.categories,
      subCategories: cachedData.subCategories,
    };
    setLocalData(enriched);
    setCachedData(newCache);

    try {
      localStorage.setItem(localCacheKey, JSON.stringify(newCache));
    } catch (e) {
      console.error("Failed to update localStorage", e);
    }
  };

const handleTogglePrescription = async (item) => {
  try {
    const newValue = Number(item.is_prescription) === 1 ? 0 : 1;

    const res =
      await productService.changePrescriptionStatusOfProduct(
        item.id,
        newValue
      );

    const updatedProducts = localData.map((product) =>
      product.id === item.id
        ? { ...product, is_prescription: newValue }
        : product
    );

    updateLocalDataCache(updatedProducts);

    showSwal(
      "Success!",
      res?.data?.message || "Prescription status updated.",
      "success"
    );
  } catch (error) {
    console.error("Prescription update failed:", error.response?.data || error);

    showSwal(
      "Error!",
      error.response?.data?.message ||
        "Failed to update prescription status.",
      "error"
    );
  }
};
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#164E4F",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
    });

    if (confirm.isConfirmed) {
      try {
        await deleteProduct(id);
        const updated = localData.filter((item) => item.id !== id);
        updateLocalDataCache(updated);
        showSwal("Deleted!", "Product has been deleted.", "success");
      } catch (error) {
        console.error("Delete error:", error.response?.data || error);
        showSwal("Error", error.response?.data?.message || "Failed to delete.", "error");
      }
    }
  };
const handleSelectAll = () => {
  if (isAllSelected) {
    setSelectedIds([]);
  } else {
    setSelectedIds(localData.map((item) => item.id));
  }
};

const handleSelectOne = (id) => {
  setSelectedIds((prev) =>
    prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]
  );
};
const handleBulkApprove = async () => {
  if (selectedIds.length === 0) {
    return showSwal("Warning", "No products selected", "warning");
  }

  try {
    await productService.bulkApproveProducts(selectedIds, 1);

    const updatedProducts = localData.map((product) =>
      selectedIds.includes(product.id)
        ? { ...product, is_approved: 1 }
        : product
    );

    updateLocalDataCache(updatedProducts);

    showSwal("Success", "Products approved successfully", "success");

    setSelectedIds([]);
  } catch (error) {
    showSwal("Error", "Failed to approve products", "error");
  }
};
const handleBulkUnapprove = async () => {
  if (selectedIds.length === 0) {
    return showSwal("Warning", "No products selected", "warning");
  }

  try {
    await productService.bulkApproveProducts(selectedIds, 0);

    const updatedProducts = localData.map((product) =>
      selectedIds.includes(product.id)
        ? { ...product, is_approved: 0 }
        : product
    );

    updateLocalDataCache(updatedProducts);

    showSwal("Success", "Products unapproved successfully", "success");

    setSelectedIds([]);
  } catch (error) {
    showSwal("Error", "Failed to unapprove products", "error");
  }
};
const handleBulkDelete = async () => {
  if (selectedIds.length === 0) {
    return showSwal("Warning", "No products selected", "warning");
  }

  const confirm = await Swal.fire({
    title: "Delete Selected Products?",
    text: "This action cannot be undone.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Delete",
  });

  if (!confirm.isConfirmed) return;

  try {
    await productService.bulkDeleteProducts(selectedIds);

    const updatedProducts = localData.filter(
      (item) => !selectedIds.includes(item.id)
    );

    updateLocalDataCache(updatedProducts);

    setSelectedIds([]);

    showSwal("Success", "Products deleted successfully", "success");
  } catch (error) {
    showSwal("Error", "Failed to delete products", "error");
  }
};
  const handleToggleStatus = async (item) => {
    try {
      const newStatus = item.status ? 0 : 1;

      await productService.changeProductStatus(item.id, newStatus); 

      const updatedProducts = localData.map((product) =>
        product.id === item.id
          ? { ...product, status: newStatus }
          : product
      );

      updateLocalDataCache(updatedProducts);

      showSwal("Success!", "Product status updated.", "success");
    } catch (error) {
      console.error("Status update failed:", error.response?.data || error);
      showSwal(
        "Error!",
        error.response?.data?.message || "Failed to update product status.",
        "error"
      );
    }
  };

console.log("productService:", productService);
  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4 ">
     <div className="d-flex justify-content-between align-items-center mb-4">
  <button
    className="btn btn-secondary me-3 "
    onClick={() => navigate(-1) || navigate("/admin/dashboard") }
  >
    Back
  </button>  


  <div className="d-flex gap-2">
    <button 
      type="button"
      className="btn btn-primary " style={{marginRight:"2rem"}}
      onClick={() => navigate("/admin/product/create")}
    >
      Create Product  
    </button>

    {/* <button
      type="button"
      className="btn btn-success"
      onClick={() => navigate("/admin/product/create-bulk")}
    >
      Create Bulk Product
    </button>  */}
  </div>
</div>

        <div className="card shadow">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0" style={{marginTop:"-1rem"}}>Seller All Products</h4>
          </div>

          <div className="card-body"> 
            <div className="mb-3 d-flex gap-2 flex-wrap"    >

<button
  onClick={handleSelectAll}
  type="button"
  style={{
    backgroundColor: isAllSelected ? "#FB6C22" : "#FB6C22",
    borderColor: isAllSelected ? "#FB6C22" : "#FB6C22",
    color: "#fff",
  }}
  className="btn"
>
  {isAllSelected ? "Unselect All" : "Select All"}
</button>

  <button
    className="btn btn-success"
    onClick={handleBulkApprove}
    type="button"
    disabled={selectedIds.length === 0}
  >
    Approve Selected ({selectedIds.length})
  </button>

  <button
    className="btn btn-warning"
    onClick={handleBulkUnapprove}
    type="button"
    disabled={selectedIds.length === 0}
  >
    Unapprove Selected
  </button>

  <button
    className="btn btn-danger"
    onClick={handleBulkDelete}
    type="button"
    disabled={selectedIds.length === 0}
  >
    Delete Selected
  </button>

</div>
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "200px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <CrudTable 

                data={localData} 
                  selectedIds={selectedIds}
  onSelectAll={handleSelectAll}
  onSelectOne={handleSelectOne}
                onEditLink={(row) => `/admin/products/edit/${row.id}`}


                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                  onTogglePrescription={handleTogglePrescription}  
                columns={[
                  "sl",
                  "thumb_image",
                  "name",
                  "price",
                  "sku",
                  "qty",
                  //  "is_prescription", 
                  "status", 
                
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
